import { useEffect, useState } from 'react';
import { getPartnerManifest, type PartnerCaptureManifest } from '../lib/api';
import { applyManifestTheme } from '../lib/manifest-theme';

interface ManifestState {
  manifest: PartnerCaptureManifest | null;
  loading: boolean;
  error: string | null;
}

/** Fetches `GET /capture/manifest` once and applies the partner's theme.
 * Failure degrades to the AICA default theme (src/index.css defaults)
 * rather than blocking the app: an operator with a signal drop mid-session
 * still gets a working capture screen, just not repainted for their
 * tenant yet. */
export function usePartnerManifest(): ManifestState {
  const [state, setState] = useState<ManifestState>({ manifest: null, loading: true, error: null });

  useEffect(() => {
    let cancelled = false;
    getPartnerManifest()
      .then((manifest) => {
        if (cancelled) return;
        applyManifestTheme(manifest);
        setState({ manifest, loading: false, error: null });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setState({
          manifest: null,
          loading: false,
          error: error instanceof Error ? error.message : 'Failed to load partner manifest',
        });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
